import { useState, useEffect, useMemo, type ReactNode } from 'react';
import apiClient from '../services/api';
import { useAuth } from './AuthContext';
import { ReportContext } from './ReportContext';

export default function ReportProvider({ children }: { children: ReactNode }) {
    const { isLoggedIn, loading: authLoading } = useAuth();
    const [fetchedData, setFetchedData] = useState<{
        reportIds: Record<string, string>;
        reportTitles: Record<string, string>;
        isFetched: boolean;
    }>({
        reportIds: {},
        reportTitles: {},
        isFetched: false,
    });

    // Derived loading state: true if auth is initializing OR if we should have reports but they aren't here yet
    const loading = authLoading || (isLoggedIn && !fetchedData.isFetched);

    useEffect(() => {
        if (authLoading || !isLoggedIn || fetchedData.isFetched) return;

        const fetchReports = async () => {
            try {
                const response = await apiClient.get<Record<string, string>>('/reports');
                const fetchedIds = response.data;

                const idToTitle: Record<string, string> = {};
                for (const name in fetchedIds) {
                    const reportId = fetchedIds[name];
                    if (reportId) {
                        idToTitle[reportId] = name;
                    }
                }

                setFetchedData({
                    reportIds: fetchedIds,
                    reportTitles: idToTitle,
                    isFetched: true,
                });
            } catch (error) {
                console.error('Failed to fetch report config', error);
                setFetchedData(prev => ({ ...prev, isFetched: true }));
            }
        };

        fetchReports();
    }, [isLoggedIn, authLoading, fetchedData.isFetched]);

    const value = useMemo(() => ({
        reportIds: fetchedData.reportIds,
        reportTitles: fetchedData.reportTitles,
        loading,
    }), [fetchedData.reportIds, fetchedData.reportTitles, loading]);

    return (
        <ReportContext.Provider value={value}>
            {children}
        </ReportContext.Provider>
    );
};
